import { useEffect, useState } from 'react';
import type { Sector } from '../../types';
import { isAdmin } from '../../auth/admins';
import { useAuthUser } from '../../auth/useAuthUser';
import {
  type ChunkQuadrant,
  type ChunkStatus,
  type IntelChunk,
  useDeleteChunk,
  useUpdateChunk,
} from '../../hooks/useIntelChunks';
import { AuditHistory } from '../admin/AuditHistory';
import {
  ChunkFormFields,
  type ChunkFormState,
  csvJoin,
  csvSplit,
} from './ChunkFormFields';
import styles from './ChunkPreview.module.css';

interface Props {
  /** The chunk to preview/edit, or null for the empty state. */
  chunk: IntelChunk | null;
  sectors: Sector[];
  /** Future meeting Mondays — populates the meeting-week dropdown. */
  meetingDates: string[];
  onAfterDelete?: () => void;
  /** Notify parent of dirty edits so it can guard navigation away. */
  onDirtyChange?: (dirty: boolean) => void;
}

const STATUS_LABEL: Record<ChunkStatus, string> = {
  draft: 'Draft',
  approved: 'Approved',
  excluded: 'Excluded',
};

const STATUS_COLOR: Record<ChunkStatus, string> = {
  draft: '#E65100',
  approved: '#2E7D32',
  excluded: '#777',
};

const QUADRANT_LABEL: Record<ChunkQuadrant, string> = {
  market_trends: 'Market Trends',
  news_regulatory: 'News & Regulatory',
  transactions: 'Transactions',
  capital_markets: 'Capital Markets',
};

const QUADRANT_COLOR: Record<ChunkQuadrant, string> = {
  market_trends: '#5F5E5A',
  news_regulatory: '#BA7517',
  transactions: '#185FA5',
  capital_markets: '#639922',
};

const CONFIDENCE_BADGE_COLOR: Record<'medium' | 'low', string> = {
  medium: '#E65100',
  low: '#C62828',
};

function chunkToFormState(chunk: IntelChunk): ChunkFormState {
  return {
    sector_id: chunk.sector_id,
    week_of: chunk.week_of,
    event_date: chunk.event_date ?? '',
    quadrant: chunk.quadrant,
    bold_lead: chunk.bold_lead,
    body: chunk.body,
    relevance_to_ember: chunk.relevance_to_ember ?? '',
    entities: csvJoin(chunk.entities),
    tags: csvJoin(chunk.tags),
    portfolio_companies: csvJoin(chunk.portfolio_companies),
    sources: chunk.sources,
  };
}

function isDirty(form: ChunkFormState, chunk: IntelChunk): boolean {
  if (form.sector_id !== chunk.sector_id) return true;
  if (form.week_of !== chunk.week_of) return true;
  if ((form.event_date || null) !== (chunk.event_date ?? null)) return true;
  if (form.quadrant !== chunk.quadrant) return true;
  if (form.bold_lead !== chunk.bold_lead) return true;
  if (form.body !== chunk.body) return true;
  if ((form.relevance_to_ember.trim() || null) !== (chunk.relevance_to_ember ?? null)) return true;
  if (csvJoin(chunk.entities) !== form.entities) return true;
  if (csvJoin(chunk.tags) !== form.tags) return true;
  if (csvJoin(chunk.portfolio_companies) !== form.portfolio_companies) return true;
  if (JSON.stringify(form.sources) !== JSON.stringify(chunk.sources)) return true;
  return false;
}

function formatEventDate(iso: string): string {
  // Press-release style: "April 13, 2026"
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

export function ChunkPreview({ chunk, sectors, meetingDates, onAfterDelete, onDirtyChange }: Props) {
  // Audit history is admin-gated — backend endpoint requires admin and
  // showing a "Failed to load" error to non-admins is uglier than just
  // not rendering the section.
  const authUser = useAuthUser();
  const showAudit = isAdmin(authUser.email);
  // Form is only meaningful in edit mode; we still maintain it across modes
  // so flipping to Edit shows the current chunk values, and Cancel can
  // reset cleanly without re-derivation.
  const [form, setForm] = useState<ChunkFormState | null>(
    chunk ? chunkToFormState(chunk) : null,
  );
  const [isEditing, setIsEditing] = useState(false);
  const [lastSyncedKey, setLastSyncedKey] = useState<string | null>(
    chunk ? `${chunk.id}-${chunk.updated_at}` : null,
  );

  // When the selected chunk changes (or its server-side updated_at advances
  // after a save), reset the form + drop edit mode. Render-time sync keeps
  // this off the effect queue.
  const currentKey = chunk ? `${chunk.id}-${chunk.updated_at}` : null;
  if (currentKey !== lastSyncedKey) {
    setLastSyncedKey(currentKey);
    setForm(chunk ? chunkToFormState(chunk) : null);
    setIsEditing(false);
  }

  const updateMut = useUpdateChunk();
  const deleteMut = useDeleteChunk();

  const dirty = !!(chunk && form && isEditing && isDirty(form, chunk));

  // Surface the dirty status up to the parent so it can guard navigation.
  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  // Always reset dirty bookkeeping when the component unmounts so the parent
  // doesn't get stuck with a stale "true" if the user closes the page.
  useEffect(() => {
    return () => onDirtyChange?.(false);
  }, [onDirtyChange]);

  if (!chunk || !form) {
    return (
      <div className={styles.empty}>
        <div className={styles.emptyTitle}>No chunk selected</div>
        <div className={styles.emptyHint}>
          Pick a chunk from the list to preview, or click <strong>+ New chunk</strong> to draft one.
        </div>
      </div>
    );
  }

  const lowConfidence =
    chunk.relevance_confidence === 'low' || chunk.relevance_confidence === 'medium';
  const showBanner = lowConfidence || !!chunk.extraction_notes;
  const isPending = updateMut.isPending || deleteMut.isPending;

  const handlePatch = (patch: Partial<ChunkFormState>) =>
    setForm((prev) => (prev ? { ...prev, ...patch } : prev));

  const handleSave = () => {
    if (!form.bold_lead.trim() || !form.body.trim()) {
      alert('Lead and body are required.');
      return;
    }
    updateMut.mutate(
      {
        id: chunk.id,
        sector_id: form.sector_id,
        week_of: form.week_of,
        event_date: form.event_date || null,
        quadrant: form.quadrant,
        bold_lead: form.bold_lead.trim(),
        body: form.body.trim(),
        relevance_to_ember: form.relevance_to_ember.trim() || null,
        entities: csvSplit(form.entities),
        tags: csvSplit(form.tags),
        portfolio_companies: csvSplit(form.portfolio_companies),
        sources: form.sources.filter((s) => s.label.trim() && s.url.trim()),
      },
      {
        onSuccess: () => setIsEditing(false), // sync hook above will refresh form
        onError: (e: Error) => alert(`Save failed: ${e.message}`),
      },
    );
  };

  const handleCancel = () => {
    if (dirty && !confirm('Discard unsaved changes?')) return;
    setForm(chunkToFormState(chunk));
    setIsEditing(false);
  };

  const handleStatusChange = (status: ChunkStatus) => {
    updateMut.mutate({ id: chunk.id, status });
  };

  const handleDelete = () => {
    if (!confirm(`Delete this chunk?`)) return;
    deleteMut.mutate(chunk.id, {
      onSuccess: () => onAfterDelete?.(),
    });
  };

  return (
    <div className={styles.preview}>
      <header className={styles.header}>
        <div className={styles.headerLeft}>
          <div className={styles.headerMeta}>
            <span className={styles.metaSector}>{chunk.sector_name ?? `Sector ${chunk.sector_id}`}</span>
            <span className={styles.metaSep}>·</span>
            <span
              className={styles.metaQuadrant}
              style={{ color: QUADRANT_COLOR[chunk.quadrant] }}
            >
              {QUADRANT_LABEL[chunk.quadrant]}
            </span>
            <span className={styles.metaSep}>·</span>
            <span
              className={styles.metaStatus}
              style={{ color: STATUS_COLOR[chunk.status] }}
            >
              {STATUS_LABEL[chunk.status]}
            </span>
            {lowConfidence && chunk.relevance_confidence !== 'high' && chunk.relevance_confidence && (
              <span
                className={styles.confidenceBadge}
                style={{
                  color: CONFIDENCE_BADGE_COLOR[chunk.relevance_confidence as 'medium' | 'low'],
                  borderColor: CONFIDENCE_BADGE_COLOR[chunk.relevance_confidence as 'medium' | 'low'],
                }}
                title={chunk.extraction_notes || 'Haiku flagged this chunk for review'}
              >
                {chunk.relevance_confidence === 'low' ? 'Low conf.' : 'Med. conf.'}
              </span>
            )}
          </div>
          <div className={styles.metaDateLine}>{chunk.week_of}</div>
        </div>
        <div className={styles.headerActions}>
          {chunk.source_url && (
            <a
              href={chunk.source_url}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.sourceLink}
              title={chunk.source_url}
            >
              source ↗
            </a>
          )}
          {!isEditing && (
            <button
              type="button"
              className={styles.editBtn}
              onClick={() => setIsEditing(true)}
              title="Edit chunk"
            >
              ✎ Edit
            </button>
          )}
        </div>
      </header>

      {showBanner && (
        <div className={styles.confidenceBanner}>
          {chunk.relevance_confidence && (
            <div className={styles.confidenceLabel}>
              Haiku flagged {chunk.relevance_confidence} confidence.
            </div>
          )}
          {chunk.extraction_notes && (
            <div className={styles.extractionNotes}>{chunk.extraction_notes}</div>
          )}
          <div className={styles.confidenceHint}>
            Review the fields below before approving.
          </div>
        </div>
      )}

      <div className={styles.body}>
        {isEditing ? (
          <ChunkFormFields
            state={form}
            sectors={sectors}
            meetingDates={meetingDates}
            onChange={handlePatch}
          />
        ) : (
          <ReadModeView chunk={chunk} />
        )}
      </div>

      <footer className={styles.footer}>
        {isEditing ? (
          <>
            <div className={styles.footerLeft}>
              <button
                type="button"
                className={styles.deleteBtn}
                onClick={handleDelete}
                disabled={isPending}
                title="Delete this chunk permanently"
              >
                Delete
              </button>
              {dirty && <span className={styles.dirtyDot}>● Unsaved changes</span>}
            </div>
            <div className={styles.footerRight}>
              <button
                type="button"
                className={styles.cancelBtn}
                onClick={handleCancel}
                disabled={isPending}
              >
                Cancel
              </button>
              <button
                type="button"
                className={styles.saveBtn}
                onClick={handleSave}
                disabled={isPending || !dirty}
                title={dirty ? 'Save changes' : 'No changes to save'}
              >
                {updateMut.isPending ? 'Saving…' : 'Save'}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className={styles.footerLeft}>
              <button
                type="button"
                className={styles.deleteBtn}
                onClick={handleDelete}
                disabled={isPending}
              >
                Delete
              </button>
            </div>
            <div className={styles.footerRight}>
              {chunk.status !== 'excluded' && (
                <button
                  type="button"
                  className={styles.excludeBtn}
                  onClick={() => handleStatusChange('excluded')}
                  disabled={isPending}
                  title="Hide from presentation; keep record"
                >
                  Exclude
                </button>
              )}
              {chunk.status === 'excluded' && (
                <button
                  type="button"
                  className={styles.reinstateBtn}
                  onClick={() => handleStatusChange('draft')}
                  disabled={isPending}
                >
                  Reinstate
                </button>
              )}
              {chunk.status === 'approved' ? (
                <button
                  type="button"
                  className={styles.unapproveBtn}
                  onClick={() => handleStatusChange('draft')}
                  disabled={isPending}
                  title="Return to draft"
                >
                  Unapprove
                </button>
              ) : (
                <button
                  type="button"
                  className={styles.approveBtn}
                  onClick={() => handleStatusChange('approved')}
                  disabled={isPending || !chunk.bold_lead.trim() || !chunk.body.trim()}
                  title={
                    !chunk.bold_lead.trim() || !chunk.body.trim()
                      ? 'Add lead + body before approving'
                      : 'Approve for presentation'
                  }
                >
                  Approve
                </button>
              )}
            </div>
          </>
        )}
      </footer>
      {showAudit && chunk && (
        <AuditHistory entityType="intel" entityId={chunk.id} />
      )}
    </div>
  );
}

/** Read-only display of the chunk content. Non-interactive — analyst clicks
 *  Edit in the header to modify. */
function ReadModeView({ chunk }: { chunk: IntelChunk }) {
  return (
    <div className={styles.readView}>
      {chunk.bold_lead ? (
        <p className={styles.readLead}>{chunk.bold_lead}</p>
      ) : (
        <p className={styles.readEmpty}>No lead written yet.</p>
      )}

      {chunk.body && (
        <p className={styles.readBody}>
          {chunk.event_date && (
            <span className={styles.readDateline}>
              {formatEventDate(chunk.event_date)} —
            </span>
          )}
          {chunk.body}
        </p>
      )}

      {chunk.relevance_to_ember && (
        <section className={styles.readSection}>
          <h4 className={styles.readSectionTitle}>Relevance to Ember</h4>
          <p className={styles.readBody}>{chunk.relevance_to_ember}</p>
        </section>
      )}

      {(chunk.entities.length > 0 || chunk.tags.length > 0 || chunk.portfolio_companies.length > 0) && (
        <section className={styles.readSection}>
          {chunk.entities.length > 0 && (
            <ChipRow label="Entities" items={chunk.entities} />
          )}
          {chunk.tags.length > 0 && (
            <ChipRow label="Tags" items={chunk.tags} mono />
          )}
          {chunk.portfolio_companies.length > 0 && (
            <ChipRow label="Portfolio companies" items={chunk.portfolio_companies} accent />
          )}
        </section>
      )}

      {chunk.sources.length > 0 && (
        <section className={styles.readSection}>
          <h4 className={styles.readSectionTitle}>Sources</h4>
          <ul className={styles.readSourceList}>
            {chunk.sources.map((s, i) => (
              <li key={i}>
                <a
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.readSourceLink}
                >
                  {s.label}
                </a>
              </li>
            ))}
          </ul>
        </section>
      )}

      <div className={styles.readMeta}>
        <span>
          Created by <strong>{chunk.created_by}</strong>
          {' · '}
          {chunk.created_at ? new Date(chunk.created_at).toLocaleString() : ''}
        </span>
        {chunk.approved_by && (
          <span>
            Approved by <strong>{chunk.approved_by}</strong>
            {chunk.approved_at && ` · ${new Date(chunk.approved_at).toLocaleString()}`}
          </span>
        )}
      </div>
    </div>
  );
}

function ChipRow({
  label,
  items,
  mono,
  accent,
}: {
  label: string;
  items: string[];
  mono?: boolean;
  accent?: boolean;
}) {
  return (
    <div className={styles.chipRow}>
      <span className={styles.chipLabel}>{label}</span>
      <div className={styles.chipList}>
        {items.map((it) => (
          <span
            key={it}
            className={`${styles.chip} ${mono ? styles.chipMono : ''} ${accent ? styles.chipAccent : ''}`}
          >
            {it}
          </span>
        ))}
      </div>
    </div>
  );
}
